import { useEffect, useMemo, useRef, useState } from "react"
import { useField } from "formik"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { CreateOrderDialogMode, MeetingServicePriceListState, SelectedPriceListItem } from "../../../createOrderTypes"
import { CreateOrderType } from "../../../helpers"
import { CreateOrderCloseConfirmDialog } from "../../CreateOrderCloseConfirmDialog"

import { EmptyPriceListsDialog } from "./EmptyPriceListsDialog"
import {
    fetchMeetingServicePriceListsForUser,
    MeetingServicePriceListsQuery,
    MeetingServicePriceListsResponse,
} from "./priceListsApi"
import type { MeetingServicePriceListsItem } from "./priceListsTypes"
import { SelectPriceListsDialog } from "./SelectPriceListsDialog"

type SelectPriceListsProps = Readonly<{
    userId: number
    onSelect: (item: SelectedPriceListItem) => void
    handleCloseMainDialog: () => void
    mode: CreateOrderDialogMode
    handleCloseSelectDialog: () => void
    orderType: CreateOrderType
    bookingOrderId?: number
}>

const EMPTY_MERCHANTS: MeetingServicePriceListsItem[] = []
const EMPTY_PRE_SELECTED_IDS: number[] = []

const transformData = (data: MeetingServicePriceListsResponse) => ({
    merchants: data.merchants,
    preSelectedMerchantIds:
        data.preSelectedMerchants?.merchants.map((merchant) => merchant.id) ?? EMPTY_PRE_SELECTED_IDS,
})

export function SelectPriceLists({
    userId,
    onSelect,
    handleCloseMainDialog,
    mode,
    handleCloseSelectDialog,
    orderType,
    bookingOrderId,
}: SelectPriceListsProps) {
    const [{ value: initPriceList }] = useField<MeetingServicePriceListState["item"]>("initPriceList")
    const [{ value: priceListState }] = useField<MeetingServicePriceListState>("priceList")
    const hasAutoSelectedRef = useRef(false)
    const [showConfirmModal, setShowConfirmModal] = useState(false)

    const { data, isLoading } = useApiQuery(fetchMeetingServicePriceListsForUser, {
        queryName: MeetingServicePriceListsQuery.USER_MEETING_SERVICE_PRICE_LISTS,
        dependencies: { userId, orderId: bookingOrderId },
        select: transformData,
        enabled: !!userId,
    })

    const merchants = data?.merchants ?? EMPTY_MERCHANTS
    const preSelectedMerchantIds = data?.preSelectedMerchantIds ?? EMPTY_PRE_SELECTED_IDS

    const filteredMerchants = useMemo(() => {
        if (mode === CreateOrderDialogMode.CREATE || !initPriceList) return merchants
        const { merchantId, shopId } = initPriceList
        return merchants.filter((m) => m.id === merchantId && m.shop?.id === shopId)
    }, [merchants, mode, initPriceList?.merchantId, initPriceList?.shopId])

    useEffect(() => {
        if (isLoading) return
        if (hasAutoSelectedRef.current) return
        if (!filteredMerchants.length) return
        if (filteredMerchants.length !== 1) return
        if (filteredMerchants[0].priceLists.length !== 1) return

        const merchant = filteredMerchants[0]
        const onlyPriceList = merchant.priceLists[0]

        hasAutoSelectedRef.current = true
        onSelect({
            merchantId: merchant.id,
            merchantName: merchant.displayName,
            priceListId: onlyPriceList.id,
            priceListName: onlyPriceList.displayName,
            shopId: merchant.shop.id,
            isOnlyOnePriceList: true,
            priceListIncludeVat: onlyPriceList.includeVat,
        })
    }, [filteredMerchants, isLoading, onSelect])

    if (isLoading) return null

    if (!filteredMerchants.length) {
        return <EmptyPriceListsDialog onClose={handleCloseMainDialog} />
    }

    if (filteredMerchants.length === 1 && filteredMerchants[0].priceLists.length === 1) {
        return null
    }
    const onClose = () => {
        if (orderType === CreateOrderType.MEETING_SERVICE && !priceListState.item) {
            setShowConfirmModal(true)
        } else handleCloseSelectDialog()
    }

    // the edit-mode list is already narrowed to a single merchant, so the nearby banner would be noise
    const isNarrowedToInitPriceList = mode !== CreateOrderDialogMode.CREATE && !!initPriceList

    return (
        <>
            <SelectPriceListsDialog
                onClose={onClose}
                merchants={filteredMerchants}
                preSelectedMerchantIds={isNarrowedToInitPriceList ? EMPTY_PRE_SELECTED_IDS : preSelectedMerchantIds}
                onSelect={onSelect}
                userId={userId}
            />
            {showConfirmModal && (
                <CreateOrderCloseConfirmDialog
                    open={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    onDiscard={handleCloseMainDialog}
                />
            )}
        </>
    )
}
